import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@progress/kendo-react-dialogs';
import I18n, { translate } from '@utils/i18n';
import api from '@utils/axios';
import { Button } from '@ui';
import toast from 'react-hot-toast';
import { markAsPaidInvoicesPath } from 'routes';

const namespace = 'features.invoices.index.mass_actions';

function MarkAsPaid({ invoiceIds, invoicesCount, disabled, queryParams }) {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentDate, setPaymentDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  const toggleDialog = () => {
    setVisible(!visible);
  };

  const onSubmit = useCallback(async () => {
    setLoading(true);

    const payload = {
      payment_date: new Date(paymentDate).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      })
    };

    if (invoiceIds) {
      const idsArray = invoiceIds.split(',');
      payload.invoice_ids = idsArray.map((id) => id.trim());
    } else {
      Object.entries(queryParams).forEach(([key, value]) => {
        payload[key] = value;
      });
    }

    try {
      const { data } = await api.post(markAsPaidInvoicesPath({ format: 'json' }), payload);

      toast.success(data.message);
      setVisible(false);
    } catch (error) {
      toast.error(error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }, [invoiceIds, queryParams, invoicesCount, paymentDate]);

  return (
    <>
      <button type="button" className="qmb-control" disabled={disabled} onClick={toggleDialog}>
        <i className="fa-light fa-check" />
        {translate('mark_as_paid', { namespace })}
      </button>
      {visible && (
        <Dialog title="Mark as Paid" className="qmb-dialog" onClose={toggleDialog} closeIcon={!loading}>
          <div className="modal-body">
            <p>
              Are you sure you want to mark {invoicesCount} invoice{invoicesCount > 1 ? 's' : ''} as paid?
            </p>

            <div className="controls" style={{ marginTop: '20px' }}>
              <div className="qmb-input">
                <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required />
                <label className="qmb-label">Payment Date</label>
              </div>
            </div>
          </div>

          <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
            <div className="mt-3">
              <Button className="mr-1" onClick={toggleDialog} disabled={loading}>
                {I18n.t('generic.cancel')}
              </Button>
              <Button variant="primary" onClick={onSubmit} disabled={loading}>
                {loading ? 'Processing...' : I18n.t('generic.ok')}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

MarkAsPaid.propTypes = {
  invoiceIds: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  invoicesCount: PropTypes.number.isRequired,
  disabled: PropTypes.bool.isRequired,
  queryParams: PropTypes.object.isRequired
};

MarkAsPaid.defaultProps = {
  invoiceIds: null
};

export default MarkAsPaid;
